import { useEffect, useState } from "react";
import { api, fmtEur, fmtDate, BACKEND_URL } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Coins, FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

function nowMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
}

export default function Payroll() {
  const [users, setUsers] = useState([]);
  const [selUser, setSelUser] = useState("");
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/users");
      const colabs = data.filter((u) => u.role === "colaborador");
      setUsers(colabs);
      if (colabs[0]) setSelUser(colabs[0].id);
    })();
  }, []);

  useEffect(() => {
    if (!selUser) return;
    api.get(`/payroll/${selUser}`, { params: { month } }).then((r)=>setData(r.data));
  }, [selUser, month]);

  const downloadPdf = () => {
    if (!selUser) return;
    fetch(`${BACKEND_URL}/api/payroll/${selUser}/pdf?month=${month}`, {
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = `liquidacion-${data?.colaborador?.name?.replace(/\s+/g, "_") || "colab"}-${month}.pdf`;
        a.click();
      })
      .catch(() => toast.error("No se pudo descargar"));
  };

  return (
    <div className="space-y-6">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-gray-500">Liquidación mensual</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Nóminas de colaboradores</h1>
      </header>

      <div className="bg-white border border-gray-200 rounded-sm p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        <div>
          <Label className="text-xs uppercase tracking-wider">Colaborador</Label>
          <Select value={selUser} onValueChange={setSelUser}>
            <SelectTrigger className="mt-1 rounded-sm" data-testid="payroll-user"><SelectValue placeholder="Selecciona" /></SelectTrigger>
            <SelectContent>{users.map((u)=> <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Mes</Label>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="mt-1 rounded-sm" data-testid="payroll-month" />
        </div>
        {data?.colaborador && (
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Retención aplicada</div>
            <div className="font-display font-bold text-xl lg:text-2xl tabular-nums truncate">{data.colaborador.retention_irpf_rate}%</div>
          </div>
        )}
        <Button onClick={downloadPdf} disabled={!data || data.totals.count === 0} className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm text-xs sm:text-sm whitespace-normal h-auto py-2 min-w-0" data-testid="payroll-pdf-btn">
          <FilePdf className="mr-1.5 shrink-0" /> <span className="truncate">Descargar PDF</span>
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Servicios</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{data.totals.count}</div></div>
            <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">Bruto (base)</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{fmtEur(data.totals.bruto)}</div></div>
            <div className="metric-card p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">+ IVA 21%</div><div className="font-display font-bold text-3xl tabular-nums mt-1">{fmtEur(data.totals.iva)}</div></div>
            <div className="metric-card p-5 bg-[#111827] text-white border-[#111827]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">Líquido a pagar</div>
              <div className="font-display font-bold text-3xl tabular-nums mt-1 text-emerald-400">{fmtEur(data.totals.neto)}</div>
              <div className="text-xs text-white/60 mt-1">-Retención {fmtEur(data.totals.retencion)}</div>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center gap-2 text-sm text-gray-600">
              <Coins weight="duotone" /> Detalle de servicios — {data.month}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 hover:bg-gray-50">
                  <TableHead className="text-[10px] uppercase tracking-[0.15em]">Fecha</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em]">Factura</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-[0.15em]">Matrícula</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Base colab.</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">IVA</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Retención</TableHead>
                  <TableHead className="text-right text-[10px] uppercase tracking-[0.15em]">Líquido</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.services.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-gray-500 py-8">Sin servicios este mes</TableCell></TableRow>}
                {data.services.map((s)=> (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs">{fmtDate(s.date)}</TableCell>
                    <TableCell className="font-mono-tab text-xs">{s.invoice_number}</TableCell>
                    <TableCell className="font-mono-tab">{s.license_plate}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtEur(s.colaborador_base)}</TableCell>
                    <TableCell className="text-right tabular-nums text-gray-500">{fmtEur(s.colaborador_iva)}</TableCell>
                    <TableCell className="text-right tabular-nums text-amber-700">-{fmtEur(s.colaborador_retencion)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{fmtEur(s.colaborador_neto)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
